import React, { useState, useEffect, useRef } from "react";
import api from "../../api";
import HrLayout from "../HrLayout";

const ExperiencePage = () => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState("");
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(false);

  // 🔽 searchable select states
  const [open, setOpen] = useState(true); // 🔥 auto open
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);

  const [formData, setFormData] = useState({
    id: null,
    title: "",
    description: "",
    date_from: "",
    date_to: "",
  });

  /* ================= LOAD EMPLOYEES ================= */
  useEffect(() => {
    api.get("/employees")
      .then(res => setEmployees(Array.isArray(res.data) ? res.data : []))
      .catch(err => console.error(err));
  }, []);

  /* ================= FETCH EXPERIENCES ================= */
  const fetchExperiences = async (empId) => {
    setLoading(true);
    try {
      const res = await api.get(`/experiences?employee_id=${empId}`);
      setExperiences(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  /* ================= ADD / UPDATE ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (formData.id) {
        await api.put(`/experiences/${formData.id}`, formData);
      } else {
        await api.post("/experiences", {
          ...formData,
          employee_id: selectedEmp,
        });
      }
      resetForm();
      fetchExperiences(selectedEmp);
    } catch {
      alert("Action failed");
    }
  };

  /* ================= DELETE ================= */
  const handleDelete = async (id) => {
    if (window.confirm("Delete experience?")) {
      await api.delete(`/experiences/${id}`);
      fetchExperiences(selectedEmp);
    }
  };

  const resetForm = () => {
    setFormData({
      id: null,
      title: "",
      description: "",
      date_from: "",
      date_to: "",
    });
  };

  /* ================= SEARCH FILTER ================= */
  const filteredEmployees = employees.filter(emp =>
    `${emp.first_name} ${emp.last_name}`
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  const selectedEmployee = employees.find(e => e.id === Number(selectedEmp));

  /* ================= CLOSE ON OUTSIDE CLICK ================= */
  useEffect(() => {
    const handler = e => {
      if (!dropdownRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* ================= UI ================= */
  return (
    <HrLayout>
      <div style={{ padding: "24px", background: "#f8fafc", minHeight: "100vh" }}>
        <h1 style={{ fontSize: "22px", fontWeight: "600", marginBottom: "20px" }}>
          Experience Management
        </h1>

        {/* ================= SEARCHABLE SELECT ================= */}
        <div
          ref={dropdownRef}
          style={{
            background: "#fff",
            padding: "16px",
            borderRadius: "8px",
            boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
            marginBottom: "20px",
            position: "relative",
          }}
        >
          <label style={{ fontWeight: "500" }}>Select Employee</label>

          <div
            onClick={() => setOpen(!open)}
            style={{
              marginTop: "8px",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "6px",
              cursor: "pointer",
              background: "#fff",
            }}
          >
            {selectedEmployee
              ? `${selectedEmployee.first_name} ${selectedEmployee.last_name}`
              : "Select Employee"}
          </div>

          {open && (
            <div
              style={{
                position: "absolute",
                width: "calc(100% - 32px)",
                background: "#fff",
                border: "1px solid #ddd",
                borderRadius: "6px",
                marginTop: "6px",
                boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
                zIndex: 20,
              }}
            >
              <input
                type="text"
                placeholder="Search employee..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px",
                  borderBottom: "1px solid #ddd",
                  outline: "none",
                }}
              />

              <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                {filteredEmployees.length === 0 && (
                  <div style={{ padding: "8px", color: "#64748b" }}>
                    No employee found
                  </div>
                )}

                {filteredEmployees.map(emp => (
                  <div
                    key={emp.id}
                    onClick={() => {
                      setSelectedEmp(emp.id);
                      setOpen(false);
                      setSearch("");
                      fetchExperiences(emp.id);
                      resetForm();
                    }}
                    style={{
                      padding: "8px",
                      cursor: "pointer",
                      borderBottom: "1px solid #f1f5f9",
                    }}
                  >
                    {emp.first_name} {emp.last_name}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {selectedEmp && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: "20px" }}>
            {/* FORM */}
            <div style={card}>
              <h3 style={{ marginBottom: "12px" }}>
                {formData.id ? "Edit Experience" : "Add Experience"}
              </h3>

              <form onSubmit={handleSubmit}>
                <input
                  type="text"
                  placeholder="Title"
                  required
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  style={inputStyle}
                />

                <textarea
                  placeholder="Description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  style={{ ...inputStyle, height: "80px" }}
                />

                <div style={{ display: "flex", gap: "10px" }}>
                  <input
                    type="date"
                    value={formData.date_from || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, date_from: e.target.value })
                    }
                    style={inputStyle}
                  />
                  <input
                    type="date"
                    value={formData.date_to || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, date_to: e.target.value })
                    }
                    style={inputStyle}
                  />
                </div>

                <div style={{ marginTop: "12px", display: "flex", gap: "10px" }}>
                  <button style={saveBtn}>Save</button>
                  <button type="button" style={clearBtn} onClick={resetForm}>
                    Clear
                  </button>
                </div>
              </form>
            </div>

            {/* LIST */}
            <div style={card}>
              <h3 style={{ marginBottom: "12px" }}>Experience List</h3>

              {loading ? (
                <p>Loading...</p>
              ) : (
                <table style={{ width: "100%" }}>
                  <thead>
                    <tr>
                      <th style={th}>Title</th>
                      <th style={th}>From</th>
                      <th style={th}>To</th>
                      <th style={th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {experiences.length === 0 ? (
                      <tr>
                        <td colSpan="4" style={empty}>No experiences found</td>
                      </tr>
                    ) : (
                      experiences.map(exp => (
                        <tr key={exp.id}>
                          <td style={td}>{exp.title}</td>
                          <td style={td}>{exp.date_from}</td>
                          <td style={td}>{exp.date_to || "-"}</td>
                          <td style={td}>
                            <button style={editBtn} onClick={() => setFormData(exp)}>Edit</button>
                            <button style={deleteBtn} onClick={() => handleDelete(exp.id)}>Delete</button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    </HrLayout>
  );
};

/* ================= STYLES ================= */
const card = {
  background: "#fff",
  padding: "20px",
  borderRadius: "8px",
  boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
};

const inputStyle = {
  width: "100%",
  padding: "8px",
  marginBottom: "10px",
  borderRadius: "6px",
  border: "1px solid #ccc",
};

const saveBtn = {
  background: "#16a34a",
  color: "#fff",
  padding: "8px 14px",
  borderRadius: "6px",
  border: "none",
};

const clearBtn = {
  background: "#e5e7eb",
  padding: "8px 14px",
  borderRadius: "6px",
  border: "none",
};

const editBtn = {
  background: "#2563eb",
  color: "#fff",
  border: "none",
  padding: "5px 10px",
  borderRadius: "4px",
  marginRight: "5px",
};

const deleteBtn = {
  background: "#dc2626",
  color: "#fff",
  border: "none",
  padding: "5px 10px",
  borderRadius: "4px",
};

const th = { padding: "8px", background: "#f1f5f9" };
const td = { padding: "8px", borderBottom: "1px solid #e5e7eb" };
const empty = { padding: "16px", textAlign: "center", color: "#64748b" };

export default ExperiencePage;
